
























































































































































































import moment from 'moment'

import { Component, Prop, Vue } from 'vue-property-decorator'
import { Action, Getter } from 'vuex-class'

import {
  CustomerInfo,
  InvoicePaymentMethodChip,
  NetworkErrorEmptyState,
  OrderItemTile,
  OrderStatusChip,
  PrintReceipt,
  RelativeTime,
  VsLoading
} from '@/components'

import {
  ListItem,
  MobileLayout,
  SkeletonBox,
  VxCard
} from '@/layouts/components'

import {
  ActionButton,
  AsyncState,
  Invoice,
  InvoiceStatus,
  ModalState,
  Order,
  OrderItem,
  OrderStatus
} from '@/models'
import { isFood } from '@/utils'

@Component({
  components: {
    CustomerInfo,
    InvoicePaymentMethodChip,
    ListItem,
    MobileLayout,
    NetworkErrorEmptyState,
    OrderItemTile,
    OrderStatusChip,
    PrintReceipt,
    RelativeTime,
    SkeletonBox,
    VxCard,
    VsLoading
  }
})
export default class OrderDetails extends Vue {
  @Prop({ type: Number, required: true })
  readonly orderId!: number

  loadState = new AsyncState()
  startState = new AsyncState()
  completeState = new AsyncState()
  cancelState = new ModalState()

  now = moment()

  get buttons (): ActionButton[] {
    return [
      {
        icon: 'print',
        color: 'dark',
        handler: window.print,
        isDisabled: this.invoice === null && this.order === null
      }
    ]
  }

  get canCancel () {
    return this.invoice !== null && this.order !== null &&
      this.invoice.status !== InvoiceStatus.CLOSED &&
      this.order.status !== OrderStatus.CANCELED &&
      this.order.status !== OrderStatus.COMPLETED
  }

  get canComplete () {
    return this.order?.status === OrderStatus.STARTED
  }

  get canInteract () {
    return this.canCancel || this.canStart || this.canComplete
  }

  get canStart () {
    return this.order?.status === OrderStatus.AUTHORIZED
  }

  get isWaiting () {
    return (
      this.startState.isPending ||
      this.completeState.isPending ||
      this.cancelState.isPending
    )
  }

  get lastInvoiceUpdateTime () {
    if (this.invoice !== null) {
      return this.invoiceLastUpdate(this.invoice)
    }
  }

  get lastOrderUpdateTime () {
    if (this.order !== null) {
      return this.orderLastUpdate(this.order)
    }
  }

  get locations () {
    return this.order
      ? this.orderLocations(this.order)
      : null
  }

  get invoice (): Invoice | null {
    return this.order
      ? this.invoiceById(this.order.invoiceId)
      : null
  }

  get order (): Order | null {
    return this.orderById(this.orderId)
  }

  get relativeTime () {
    return this.order &&
      moment(this.order.createdAt).startOf('day')
        .isSame(this.now.clone().startOf('day'))
  }

  get title () {
    if (this.order !== null) {
      return `Pedido #${this.order.code}`
    }

    return this.loadState.isPending
      ? null
      : 'Detalhes do pedido'
  }

  get invoiceTitle () {
    return isFood() ? 'Comanda' : 'Compra'
  }

  @Getter('ordering/invoiceById')
  readonly invoiceById!: (id: number) => Invoice | null

  @Getter('ordering/invoiceLastUpdate')
  readonly invoiceLastUpdate!: (invoice: Invoice) => Date

  @Getter('ordering/orderById')
  readonly orderById!: (id: number) => Order | null

  @Getter('ordering/orderLastUpdate')
  readonly orderLastUpdate!: (order: Order) => Date

  @Getter('ordering/orderLocations')
  readonly orderLocations!: (order: Order) => string[] | null

  @Action('ordering/loadOrder')
  loadOrder!: (id: number) => Promise<void>

  @Action('ordering/cancelOrder')
  cancelOrder!: (id: number) => Promise<void>

  @Action('ordering/completeOrder')
  completeOrder!: (id: number) => Promise<void>

  @Action('ordering/startOrder')
  startOrder!: (id: number) => Promise<void>

  created () {
    if (this.order === null) {
      this.reload()
    } else {
      this.loadState.touch()
    }
  }

  async reload () {
    try {
      await this.loadState.handle(
        this.loadOrder(this.orderId)
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  async handleStartOrder () {
    try {
      await this.startState.handle(
        this.startOrder(this.orderId)
      )
    } catch (e) {
      this.$vs.notify({
        title: 'Não foi possível iniciar o pedido.',
        color: 'danger',
        fullWidth: true
      })

      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  async handleCompleteOrder () {
    try {
      await this.completeState.handle(
        this.completeOrder(this.orderId)
      )

      this.$vs.notify({
        title: 'Pedido completado.',
        color: 'success',
        fullWidth: true
      })
    } catch (e) {
      this.$vs.notify({
        title: 'Não foi possível completar o pedido.',
        color: 'danger',
        fullWidth: true
      })

      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  async handleCancelOrder () {
    try {
      await this.cancelState.handle(
        this.cancelOrder(this.orderId)
      )

      this.$vs.notify({
        title: 'Pedido cancelado.',
        color: 'success',
        fullWidth: true
      })
    } catch (e) {
      this.$vs.notify({
        title: 'Não foi possível cancelar.',
        color: 'danger',
        fullWidth: true
      })

      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  itemKey (item: OrderItem, index: number) {
    const repeated = this.order
      ? this.order.items.filter((i) => i.id === item.id).length > 1
      : false

    return `${item.id}${repeated ? `_${index}` : ''}`
  }
}
